import { chakra, Flex, Span, useSlotRecipe } from '@chakra-ui/react';
import type { DateValue } from '@internationalized/date';

import * as React from 'react';
import {
  Button as AriaButton,
  Calendar as AriaCalendar,
  CalendarCell as AriaCalendarCell,
  CalendarGrid as AriaCalendarGrid,
  CalendarGridBody as AriaCalendarGridBody,
  CalendarGridHeader as AriaCalendarGridHeader,
  CalendarHeaderCell as AriaCalendarHeaderCell,
  DateInput as AriaDateInput,
  DatePicker as AriaDatePicker,
  DateSegment as AriaDateSegmentComponent,
  Dialog as AriaDialog,
  FieldError as AriaFieldError,
  Group as AriaGroup,
  Heading as AriaHeading,
  Label as AriaLabel,
  Popover as AriaPopover,
  Text as AriaText,
} from 'react-aria-components';
import type { DateSegment } from 'react-stately';
import { datePickerRecipe } from '@fluenceur/theme';
import { Calendar, NavArrowLeft, NavArrowRight } from 'iconoir-react';

// Factory components
const ChakraLabel = chakra(AriaLabel);
const ChakraGroup = chakra(AriaGroup);
const ChakraDateInput = chakra(AriaDateInput);
const ChakraDateSegment = chakra(AriaDateSegmentComponent);
const ChakraButton = chakra(AriaButton);
const ChakraFieldError = chakra(AriaFieldError);
const ChakraPopover = chakra(AriaPopover);
const ChakraDialog = chakra(AriaDialog);
const ChakraCalendar = chakra(AriaCalendar);
const ChakraCalendarGrid = chakra(AriaCalendarGrid);
const ChakraCalendarGridHeader = chakra(AriaCalendarGridHeader);
const ChakraCalendarCell = chakra(AriaCalendarCell);

interface AriaDatePickerProps {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: any) => string);
  minValue?: DateValue;
  value?: DateValue | null;
  onChange?: (value: DateValue | null) => void;
  isInvalid?: boolean;
  isDisabled?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export const DatePicker: React.FC<AriaDatePickerProps> = (props) => {
  const recipe = useSlotRecipe({ recipe: datePickerRecipe });
  const styles = recipe({});

  const {
    label,
    description,
    errorMessage,
    minValue,
    value,
    onChange,
    isInvalid,
    isDisabled,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...restProps
  } = props;

  // Determine appropriate aria attributes for accessibility
  const accessibilityProps: {
    'aria-label'?: string;
    'aria-labelledby'?: string;
  } = {};

  if (!label) {
    // If no visible label is provided, we need aria-label or aria-labelledby for accessibility
    if (ariaLabel) {
      accessibilityProps['aria-label'] = ariaLabel;
    } else if (ariaLabelledBy) {
      accessibilityProps['aria-labelledby'] = ariaLabelledBy;
    } else {
      // Provide a default aria-label as fallback
      accessibilityProps['aria-label'] = 'Select date';
    }
  }

  return (
    <AriaDatePicker
      {...restProps}
      {...accessibilityProps}
      minValue={minValue as any}
      value={value as any}
      onChange={onChange as any}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      shouldCloseOnSelect={true}
    >
      {label && <ChakraLabel css={styles.label}>{label}</ChakraLabel>}
      <ChakraGroup css={styles.group}>
        <Flex flex={1} flexDir="column">
          <Span css={styles.dateInputPlaceholder}>Select date</Span>
          <ChakraDateInput css={styles.dateInput}>
            {(segment: DateSegment) => (
              <ChakraDateSegment css={styles.dateSegment} segment={segment} />
            )}
          </ChakraDateInput>
        </Flex>
        <ChakraButton css={styles.button}>
          <Calendar width={20} height={20} />
        </ChakraButton>
      </ChakraGroup>
      {description && !isInvalid && <AriaText slot="description">{description}</AriaText>}
      {isInvalid && errorMessage && (
        <ChakraFieldError css={styles.errorText}>{errorMessage}</ChakraFieldError>
      )}
      <ChakraPopover placement="bottom start" css={styles.popover}>
        <ChakraDialog css={styles.dialog}>
          <ChakraCalendar css={styles.calendar}>
            <chakra.header css={styles.calendarHeader}>
              <ChakraButton slot="previous">
                <NavArrowLeft width={16} height={16} />
              </ChakraButton>
              <AriaHeading />
              <ChakraButton slot="next">
                <NavArrowRight width={16} height={16} />
              </ChakraButton>
            </chakra.header>
            <ChakraCalendarGrid css={styles.calendarGrid}>
              <ChakraCalendarGridHeader css={styles.calendarHeaderCell}>
                {(day: string) => <AriaCalendarHeaderCell>{day}</AriaCalendarHeaderCell>}
              </ChakraCalendarGridHeader>
              <AriaCalendarGridBody>
                {(date) => (
                  <ChakraCalendarCell css={styles.calendarCell} date={date}>
                    {({ formattedDate }: { formattedDate: string }) => <>{formattedDate}</>}
                  </ChakraCalendarCell>
                )}
              </AriaCalendarGridBody>
            </ChakraCalendarGrid>
          </ChakraCalendar>
        </ChakraDialog>
      </ChakraPopover>
    </AriaDatePicker>
  );
};
